import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PlusCircle, Edit, Trash2, Zap, Star, Settings } from 'lucide-react';
import { modelsApi } from '../../api/models';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { TierModelAccessManager } from '../../components/admin/TierModelAccessManager';

export const AIModelsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<any>(null);
  const [selectedModelForTiers, setSelectedModelForTiers] = useState<any>(null);

  // Fetch models
  const { data: models = [], isLoading } = useQuery({
    queryKey: ['admin', 'models'],
    queryFn: () => modelsApi.getAllModels(false),
  });

  // Create model mutation
  const createMutation = useMutation({
    mutationFn: modelsApi.createModel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'models'] });
      toast.success('Model created successfully');
      setIsCreateDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create model');
    },
  });

  // Update model mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      modelsApi.updateModel(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'models'] });
      toast.success('Model updated successfully');
      setEditingModel(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update model');
    },
  });

  // Delete model mutation
  const deleteMutation = useMutation({
    mutationFn: modelsApi.deleteModel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'models'] });
      toast.success('Model deactivated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete model');
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name') as string,
      display_name: formData.get('display_name') as string,
      description: formData.get('description') as string,
      provider: formData.get('provider') as string,
      model_identifier: formData.get('model_identifier') as string,
      base_credit_cost: parseFloat(formData.get('base_credit_cost') as string),
      speed_rating: parseInt(formData.get('speed_rating') as string) || null,
      quality_rating: parseInt(formData.get('quality_rating') as string) || null,
      badge_text: formData.get('badge_text') as string || null,
      capabilities: {},
    };

    if (editingModel) {
      updateMutation.mutate({ id: editingModel.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">AI Models Management</h1>
          <p className="text-muted-foreground">
            Manage AI models and their tier access
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <PlusCircle className="w-4 h-4 mr-2" />
          Add Model
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Available Models</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div>Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Base Cost</TableHead>
                  <TableHead>Speed</TableHead>
                  <TableHead>Quality</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {models.map((model: any) => (
                  <TableRow key={model.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{model.display_name}</div>
                        <div className="text-sm text-muted-foreground">
                          {model.name}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{model.provider}</TableCell>
                    <TableCell>{model.base_credit_cost} credits</TableCell>
                    <TableCell>
                      {model.speed_rating && (
                        <div className="flex items-center">
                          <Zap className="w-4 h-4 mr-1 text-yellow-500" />
                          {model.speed_rating}/5
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {model.quality_rating && (
                        <div className="flex items-center">
                          <Star className="w-4 h-4 mr-1 text-orange-500" />
                          {model.quality_rating}/5
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          model.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {model.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingModel(model)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedModelForTiers(model)}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            if (
                              confirm(
                                'Are you sure you want to deactivate this model?'
                              )
                            ) {
                              deleteMutation.mutate(model.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Model Dialog */}
      <Dialog
        open={isCreateDialogOpen || !!editingModel}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateDialogOpen(false);
            setEditingModel(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingModel ? 'Edit Model' : 'Create Model'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name (Internal)</Label>
              <Input
                id="name"
                name="name"
                defaultValue={editingModel?.name}
                required
                placeholder="gemini-flash"
              />
            </div>
            <div>
              <Label htmlFor="display_name">Display Name</Label>
              <Input
                id="display_name"
                name="display_name"
                defaultValue={editingModel?.display_name}
                required
                placeholder="Gemini 2.5 Flash"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                defaultValue={editingModel?.description}
                placeholder="Fast and efficient image generation"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="provider">Provider</Label>
                <Input
                  id="provider"
                  name="provider"
                  defaultValue={editingModel?.provider}
                  required
                  placeholder="gemini"
                />
              </div>
              <div>
                <Label htmlFor="model_identifier">Model Identifier</Label>
                <Input
                  id="model_identifier"
                  name="model_identifier"
                  defaultValue={editingModel?.model_identifier}
                  required
                  placeholder="gemini-2.5-flash-image"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="base_credit_cost">Base Cost (Credits)</Label>
                <Input
                  id="base_credit_cost"
                  name="base_credit_cost"
                  type="number"
                  step="0.1"
                  defaultValue={editingModel?.base_credit_cost || 5}
                  required
                />
              </div>
              <div>
                <Label htmlFor="speed_rating">Speed (1-5)</Label>
                <Input
                  id="speed_rating"
                  name="speed_rating"
                  type="number"
                  min="1"
                  max="5"
                  defaultValue={editingModel?.speed_rating}
                />
              </div>
              <div>
                <Label htmlFor="quality_rating">Quality (1-5)</Label>
                <Input
                  id="quality_rating"
                  name="quality_rating"
                  type="number"
                  min="1"
                  max="5"
                  defaultValue={editingModel?.quality_rating}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="badge_text">Badge Text</Label>
              <Input
                id="badge_text"
                name="badge_text"
                defaultValue={editingModel?.badge_text}
                placeholder="Fast, Premium, etc."
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  setEditingModel(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingModel ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tier Access Manager Dialog */}
      {selectedModelForTiers && (
        <TierModelAccessManager
          model={selectedModelForTiers}
          onClose={() => setSelectedModelForTiers(null)}
        />
      )}
    </div>
  );
};
